import {
  AfterViewInit,
  Directive,
  DoCheck,
  ElementRef,
  HostBinding,
  Input,
  computed,
  effect,
  inject,
  signal,
  untracked,
  Provider,
} from '@angular/core';
import { FormGroupDirective, NgControl, NgForm } from '@angular/forms';

import { createClearButton } from './input.directive.util';
import { TT_INPUT_HOST } from './tt-input.token';
import { FormFieldControl } from '../form-field/form-field-control';
import { ErrorStateMatcher } from '../forms/error-options';
import { ErrorStateTracker } from '../forms/error-state-tracker';

@Directive({
  selector: '[ttInput]',
  standalone: true,
  providers: [
    {
      provide: FormFieldControl,
      useExisting: InputDirective,
    },
    {
      provide: TT_INPUT_HOST,
      useExisting: InputDirective,
    } as Provider,
  ],
})
export class InputDirective implements FormFieldControl, DoCheck, AfterViewInit {
  public ngControl = inject(NgControl, { optional: true, self: true }) as NgControl | null;

  private defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private parentForm = inject(NgForm, { optional: true });
  private parentFormGroup = inject(FormGroupDirective, { optional: true });
  private host = inject(ElementRef<HTMLElement>);

  private readonly _error = signal(false);
  private readonly _noBorder = signal(false);

  @Input()
  public set error(value: boolean) {
    this._error.set(value);
  }

  @Input()
  public set noBorder(value: boolean) {
    this._noBorder.set(value);
  }

  @Input() public clearable = false;

  @HostBinding('class.tt-input') baseClass = true;

  @HostBinding('class.tt-input--error')
  get errorClass() {
    return this._error();
  }

  @HostBinding('class.tt-input--disabled')
  get isDisabled() {
    return this.ngControl?.control?.disabled ?? false;
  }

  @HostBinding('class.tt-input--no-border')
  get noBorderClass() {
    return this._noBorder();
  }

  public errorState = computed(() => this.errorStateTracker?.errorState?.() ?? false);

  private errorStateTracker!: ErrorStateTracker;

  constructor() {
    this.initErrorStateTrackerIfNeeded();

    effect(() => {
      if (this.ngControl) {
        const hasError = this.errorStateTracker.errorState();
        untracked(() => this._error.set(hasError));
      } else {
        untracked(() => this._error.set(false));
      }
    });
  }

  public registerNgControl(control: NgControl) {
    if (this.ngControl === control) return;

    this.ngControl = control;
    this.initErrorStateTrackerIfNeeded();

    this.errorStateTracker.updateErrorState();
    this.syncDisabledState();
  }

  private initErrorStateTrackerIfNeeded() {
    this.errorStateTracker = new ErrorStateTracker(this.defaultErrorStateMatcher, this.ngControl, this.parentFormGroup, this.parentForm);
  }

  public ngAfterViewInit() {
    if (!this.clearable) return;

    const button = createClearButton();

    button.addEventListener('click', () => this.clearValue());

    const hostEl = this.host.nativeElement;
    if (getComputedStyle(hostEl).position === 'static') {
      hostEl.style.position = 'relative';
    }

    hostEl.appendChild(button);

    const input = this.getInputElement();
    if (input) {
      this.updateButtonVisibility(button, input.value);
      input.addEventListener('input', () => {
        this.updateButtonVisibility(button, input.value);
      });
    }
  }

  public clearValue() {
    const input = this.getInputElement();

    if (input) {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  public ngDoCheck() {
    this.errorStateTracker?.updateErrorState();
    this.syncDisabledState();
  }

  private syncDisabledState() {
    const input = this.getInputElement();
    if (!input || !this.ngControl) return;

    const isDisabled = this.ngControl.control?.disabled ?? false;

    if (isDisabled) {
      input.setAttribute('disabled', '');
    } else {
      input.removeAttribute('disabled');
    }
  }

  private getInputElement(): HTMLInputElement | null {
    const hostEl = this.host.nativeElement;
    if (hostEl.tagName === 'INPUT') return hostEl as HTMLInputElement;
    return hostEl.querySelector('input');
  }

  private updateButtonVisibility(button: HTMLButtonElement, value: string) {
    button.style.display = value ? 'flex' : 'none';
  }
}
